import classNames from 'classnames';
import React from 'react';

import {TrackImpression} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Tag} from '@udemy/react-product-card-components';
import {SuspenseUntilInView, TextSkeleton} from '@udemy/react-reveal-components';
import {noop} from '@udemy/shared-utils';

import {useDevice} from 'src/hooks/useDevice';

import {useEnrollmentDisplay} from '../../hooks/useEnrollmentDisplay';
import styles from './topic-card.module.less';

interface TopicCardProps {
    /**
     * HTML tag to use for the Card.
     *
     * @defaultValue 'h4'
     */
    headingElement?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /**
     * The title of the card.
     */
    title?: string;
    /**
     * The background image URL for the card.
     */
    backgroundImageUrl?: string;
    /**
     * The URL to navigate to when the card is clicked.
     */
    href?: string;
    /**
     * The function to call when the card is clicked.
     */
    onClick?: () => void;
    /**
     * The number of learners for the card topic.
     */
    num_enrollments?: number;
    /**
     * The function to call when the card is tracked.
     */
    trackImpression?: () => void;
}

export const TopicCard = ({
    headingElement = 'h4',
    title,
    backgroundImageUrl,
    href,
    onClick,
    num_enrollments,
    trackImpression,
}: TopicCardProps) => {
    const {direction} = useI18n();
    const {renderEnrollments} = useEnrollmentDisplay();
    const HeadingElement = headingElement;
    const {isMobile} = useDevice();

    const iconGlyph = direction === 'rtl' ? 'arrow-left' : 'arrow-right';

    const renderTitle = (title: string | undefined) => {
        if (title) {
            return (
                <HeadingElement
                    className={classNames(styles.title, isMobile ? 'ud-text-sm' : 'ud-text-lg')}
                >
                    {title}
                </HeadingElement>
            );
        }
        return (
            <TextSkeleton
                className={classNames(
                    styles.title,
                    styles['title-loading'],
                    isMobile ? 'ud-text-sm' : 'ud-text-lg',
                )}
                lineCountPerParagraph={1}
                paragraphCount={1}
            />
        );
    };

    return (
        <SuspenseUntilInView fallback={null}>
            <TrackImpression trackFunc={trackImpression ?? noop}>
                <a
                    className={classNames(styles['topic-card'])}
                    style={{backgroundImage: `url(${backgroundImageUrl})`}}
                    href={href}
                    onClick={onClick}
                >
                    <div className={styles['info']}>
                        {num_enrollments !== 0 && (
                            <Tag>
                                <Icon glyph="people" label={false} />
                                {renderEnrollments(
                                    num_enrollments,
                                    styles['enrollments-loading'],
                                    true,
                                )}
                            </Tag>
                        )}
                        <div className={styles['title-container']}>
                            {renderTitle(title)}
                            {!isMobile && (
                                <div className={styles['icon-container']}>
                                    <Icon glyph={iconGlyph} label={false} />
                                </div>
                            )}
                        </div>
                    </div>
                </a>
            </TrackImpression>
        </SuspenseUntilInView>
    );
};
